// src/laborRules.js
// Pure rules that decide whether one cost line is labor or materials. No React, no database.
//
// Every cost line is exactly one of the two, never both, so on every job
//   labor + materials = total costs, to the cent, and profit and margin never change because of this.
//
// What decides it, strongest first:
//   1. The owner's choice for who was paid (a vendor rule: labor or not labor).
//   2. The owner's choice for the QuickBooks category (an account rule: labor or not labor).
//   3. The default: the category name contains one of LABOR_KEYWORDS as a whole word.
//   4. Otherwise the cost counts as materials (also when there is no category name).
// The category name is captured from QuickBooks at sync. Rows synced before that have no category, so
// only rule 1 can make them labor until the next sync.
//
// Canopy-made cost rows (a review item assigned to a job in Canopy) carry no category of their own.
// They use the category stored on the review item they came from (see accountForTransaction).

import { toWholeCents } from "./money";
import { EXPENSE_CATEGORIES } from "./lib/constants";

// Whole-word, case-insensitive. A category like "Job Costs:Subcontractors" is split into words at every
// character that is not a letter, so "Subcontractors", "Sub-contractor" and "Contract labor" all match.
// "Contract labor", "casual labor" and "subcontract" are covered by "labor" and "subcontract".
export const LABOR_KEYWORDS = [
  "labor", "labour", "wage", "wages", "payroll", "salary", "salaries",
  "subcontract", "subcontractor", "subcontractors", "contractors",
  "crew", "freelance", "freelancer", "freelancers",
];

// Lower case, single spaces, no edge spaces. Used to compare names; never shown.
export function normalizeName(s) {
  return String(s ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

// True when the category name contains a labor keyword as a whole word.
export function isLaborByKeyword(accountName) {
  const words = normalizeName(accountName).split(/[^a-z]+/).filter(Boolean);
  return words.some((w) => LABOR_KEYWORDS.includes(w));
}

// Turns the saved rows ({ kind: 'account' | 'vendor', name, is_labor }) into a lookup.
// Bad rows are skipped. If a name somehow appears twice, the last row wins.
export function buildLaborLookup(rows) {
  const account = new Map();
  const vendor = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r || typeof r.is_labor !== "boolean") return;
    const key = normalizeName(r.name);
    if (!key) return;
    if (r.kind === "account") account.set(key, r.is_labor);
    else if (r.kind === "vendor") vendor.set(key, r.is_labor);
  });
  return { account, vendor };
}

// One cost line -> { isLabor, reason }. reason says which rule decided:
// 'vendor' | 'account' | 'keyword' | 'default' (materials, nothing matched).
export function classifyCost({ vendor, accountName } = {}, lookup = buildLaborLookup([])) {
  const v = normalizeName(vendor);
  if (v && lookup.vendor.has(v)) return { isLabor: lookup.vendor.get(v), reason: "vendor" };
  const a = normalizeName(accountName);
  if (a && lookup.account.has(a)) return { isLabor: lookup.account.get(a), reason: "account" };
  if (a && isLaborByKeyword(a)) return { isLabor: true, reason: "keyword" };
  return { isLabor: false, reason: "default" };
}

// Cash costs the owner adds in Canopy (manual_expenses) follow the very same decision as QuickBooks costs.
// Their category is saved as a short value ("subcontractor"); the owner sees and chooses it by its label
// ("Subcontractor"). The label is the category name used for the owner's choice (labor_rules kind 'account',
// compared without regard to capital letters, so it is the same choice as a QuickBooks category of that name).
// An unknown value is used as typed. No category counts as "Other".
export function expenseCategoryName(value) {
  const v = normalizeName(value);
  if (!v) return "Other";
  const hit = EXPENSE_CATEGORIES.find((c) => c.value === v || normalizeName(c.label) === v);
  return hit ? hit.label : String(value).trim();
}

// One cash cost { vendor, category } -> { isLabor, reason }, same order as classifyCost:
// the owner's choice for the vendor, then for the category, then the keyword default, then materials.
export function classifyExpense(exp, lookup = buildLaborLookup([])) {
  return classifyCost({ vendor: exp?.vendor, accountName: expenseCategoryName(exp?.category) }, lookup);
}

// The QuickBooks category for a cost row. QuickBooks rows carry it themselves (account_name). A Canopy
// cost row has the id {user}_inbox_{reviewId} or {user}_automatch_{reviewId}; its category is the one
// stored on that review item. reviewAccounts: { reviewId: accountName }. Returns '' when unknown.
export function accountForTransaction(txn, userId, reviewAccounts = {}) {
  if (!txn) return "";
  if (txn.account_name) return String(txn.account_name);
  const id = String(txn.id || "");
  for (const prefix of [`${userId}_inbox_`, `${userId}_automatch_`]) {
    if (userId && id.startsWith(prefix)) {
      const name = reviewAccounts[id.slice(prefix.length)];
      return name ? String(name) : "";
    }
  }
  return "";
}

// Splits lines into labor and materials, in whole cents. Each line is rounded to cents once and goes
// to exactly one side, so laborCents + materialCents = the sum of the rounded lines.
//   lines: [{ amount, vendor, accountName }]
export function splitCostCents(lines, lookup) {
  let laborCents = 0, materialCents = 0;
  (lines || []).forEach((l) => {
    const c = toWholeCents(Number(l?.amount) || 0);
    if (classifyCost(l, lookup).isLabor) laborCents += c; else materialCents += c;
  });
  return { laborCents, materialCents, totalCents: laborCents + materialCents };
}

// What the settings screen shows: the categories and the people/companies found in this business's
// costs, each with its total in whole cents and whether it counts as labor now.
//   transactions: cost rows ({ id, type, vendor, amount, account_name }) - only type 'expense' is used.
//   cashCosts: costs added in Canopy ({ vendor, amount, category }); their categories and vendors are listed too, joined
//     with the QuickBooks ones when the names match. offeredCategories: category names to list even when unused
//     (the ones the Add expense form offers); they show with 0 cents.
//   reviewAccounts: { reviewId: accountName } from the review items. rules: saved rows. userId: this business.
// Returns { accounts, vendors, withoutCategoryCents, withoutCategoryCount }, biggest amount first.
//   accounts: [{ name, cents, isLabor, byKeyword, hasRule }]   (isLabor ignores vendor rules: it is the category choice)
//   vendors:  [{ name, cents, choice: 'labor' | 'not_labor' | 'auto' }]
export function summarizeLaborSources({ transactions, reviewAccounts, rules, userId, cashCosts, offeredCategories }) {
  const lookup = buildLaborLookup(rules);
  const accounts = new Map();
  const vendors = new Map();
  let withoutCategoryCents = 0, withoutCategoryCount = 0;
  (transactions || []).forEach((t) => {
    if (!t || t.type !== "expense") return;
    const c = toWholeCents(Number(t.amount) || 0);
    const acct = accountForTransaction(t, userId, reviewAccounts);
    const ak = normalizeName(acct);
    if (ak) {
      const a = accounts.get(ak) || { name: String(acct).trim(), cents: 0 };
      a.cents += c; accounts.set(ak, a);
    } else { withoutCategoryCents += c; withoutCategoryCount++; }
    const vname = String(t.vendor ?? "").trim();
    const vk = normalizeName(vname);
    if (vk && vk !== "unknown vendor") {
      const v = vendors.get(vk) || { name: vname, cents: 0 };
      v.cents += c; vendors.set(vk, v);
    }
  });
  (cashCosts || []).forEach((e) => {
    if (!e) return;
    const c = toWholeCents(Number(e.amount) || 0);
    const cname = expenseCategoryName(e.category);
    const ck = normalizeName(cname);
    const a = accounts.get(ck) || { name: cname, cents: 0 };
    a.cents += c; accounts.set(ck, a);
    const vname = String(e.vendor ?? "").trim();
    const vk = normalizeName(vname);
    if (vk && vk !== "unknown vendor") {
      const v = vendors.get(vk) || { name: vname, cents: 0 };
      v.cents += c; vendors.set(vk, v);
    }
  });
  (offeredCategories || []).forEach((name) => {
    const k = normalizeName(name);
    if (k && !accounts.has(k)) accounts.set(k, { name: String(name).trim(), cents: 0 });
  });
  const byBig = (x, y) => y.cents - x.cents || x.name.localeCompare(y.name);
  return {
    accounts: [...accounts.entries()].map(([k, a]) => {
      const hasRule = lookup.account.has(k);
      const byKeyword = isLaborByKeyword(k);
      return { name: a.name, cents: a.cents, byKeyword, hasRule, isLabor: hasRule ? lookup.account.get(k) : byKeyword };
    }).sort(byBig),
    vendors: [...vendors.entries()].map(([k, v]) => ({
      name: v.name, cents: v.cents,
      choice: lookup.vendor.has(k) ? (lookup.vendor.get(k) ? "labor" : "not_labor") : "auto",
    })).sort(byBig),
    withoutCategoryCents, withoutCategoryCount,
  };
}

// Works out what to write so the saved rules match what the owner chose.
//   kind: 'account' | 'vendor'.  existing: saved rows { id, kind, name, is_labor }.
//   desired: [{ name, isLabor }] where isLabor is true, false, or null (no rule: follow the default).
// For categories, a choice equal to the keyword default needs no rule, so it is removed.
// Returns { inserts: [{kind,name,is_labor}], updates: [{id,is_labor}], deletes: [id] }. Only changes are listed.
export function planRuleChanges(kind, existing, desired) {
  const mine = new Map();
  (existing || []).filter((r) => r && r.kind === kind).forEach((r) => mine.set(normalizeName(r.name), r));
  const out = { inserts: [], updates: [], deletes: [] };
  (desired || []).forEach((d) => {
    const key = normalizeName(d.name);
    if (!key) return;
    const wanted = kind === "account" && d.isLabor === isLaborByKeyword(key) ? null : d.isLabor;
    const have = mine.get(key);
    if (wanted === null || wanted === undefined) {
      if (have) out.deletes.push(have.id);
    } else if (!have) {
      out.inserts.push({ kind, name: String(d.name).trim(), is_labor: wanted });
    } else if (have.is_labor !== wanted) {
      out.updates.push({ id: have.id, is_labor: wanted });
    }
  });
  return out;
}
